<script setup lang="ts">
import type { User } from '@/types'

import { Form, Head, Link, usePage } from '@inertiajs/vue3'
import SettingsCard from '@/components/settings/SettingsCard.vue'
import SettingsPanel from '@/components/settings/SettingsPanel.vue'
import { firstFormError } from '@/lib/forms'
import { dashboardLayout } from '@/lib/layouts'
import { adminSettingsEmailPath, identityEmailVerificationPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})
const page = usePage()
const user = page.props.auth.user as User
</script>

<template>
  <Head title="Email settings" />

  <SettingsPanel
    title="Settings"
    description="Manage profile, security, sessions, and appearance preferences."
  >
    <SettingsCard
      title="Email address"
      description="Change the email used for login, notices, and account recovery."
      icon="i-lucide-mail"
    >
      <Form
        v-slot="{ errors, processing, recentlySuccessful }"
        method="patch"
        :action="adminSettingsEmailPath()"
        :options="{ preserveScroll: true }"
        :reset-on-error="['password_challenge']"
        :reset-on-success="['password_challenge']"
        class="max-w-2xl space-y-5"
      >
        <UFormField
          label="Email address"
          name="email"
          :error="firstFormError(errors.email)"
          required
        >
          <UInput
            id="email"
            name="email"
            type="email"
            :default-value="user.email"
            autocomplete="username"
            placeholder="name@company.com"
            icon="i-lucide-mail"
            class="w-full"
          />
        </UFormField>

        <UAlert
          v-if="!user.verified"
          color="warning"
          variant="subtle"
          icon="i-lucide-circle-alert"
          title="Email address is unverified"
          description="Resend the verification message to complete account setup."
        >
          <template #actions>
            <UButton
              :as="Link"
              :href="identityEmailVerificationPath()"
              method="post"
              color="warning"
              variant="soft"
              label="Resend verification"
            />
          </template>
        </UAlert>

        <UFormField
          label="Current password"
          name="password_challenge"
          :error="firstFormError(errors.password_challenge)"
          required
        >
          <UInput
            id="password_challenge"
            name="password_challenge"
            type="password"
            autocomplete="current-password"
            placeholder="Enter your current password"
            icon="i-lucide-lock-keyhole"
            class="w-full"
          />
        </UFormField>

        <div class="flex items-center gap-3">
          <UButton
            type="submit"
            label="Save email"
            icon="i-lucide-save"
            :loading="processing"
          />

          <Transition
            enter-active-class="transition ease-in-out"
            enter-from-class="opacity-0"
            leave-active-class="transition ease-in-out"
            leave-to-class="opacity-0"
          >
            <UBadge
              v-show="recentlySuccessful"
              color="success"
              variant="subtle"
              icon="i-lucide-check"
            >
              Saved
            </UBadge>
          </Transition>
        </div>
      </Form>
    </SettingsCard>
  </SettingsPanel>
</template>
